import { APP_CONSTANTS } from '../config/constants';

type TableDefinition = {
  NAME: string;
  COLUMNS: Array<{ name: string }>;
};

export interface SanitizedLocalTablePayload<T extends Record<string, any> = Record<string, any>> {
  data: Partial<T>;
  droppedKeys: string[];
  tableKnown: boolean;
}

const getTableDefinition = (tableName: string): TableDefinition | null => {
  const tableDefinition = Object.values(APP_CONSTANTS.DATABASE.TABLES)
    .find((definition: any) => definition.NAME === tableName) as TableDefinition | undefined;

  return tableDefinition ?? null;
};

export const getLocalTableColumnNames = (tableName: string): string[] => {
  const tableDefinition = getTableDefinition(tableName);
  return tableDefinition?.COLUMNS.map((column) => column.name) ?? [];
};

export const sanitizeLocalTablePayload = <T extends Record<string, any>>(
  tableName: string,
  payload: T
): SanitizedLocalTablePayload<T> => {
  const allowedColumns = new Set(getLocalTableColumnNames(tableName));

  if (allowedColumns.size === 0 || !payload || typeof payload !== 'object') {
    return {
      data: payload,
      droppedKeys: [],
      tableKnown: allowedColumns.size > 0,
    };
  }

  const data: Record<string, any> = {};
  const droppedKeys: string[] = [];

  Object.entries(payload).forEach(([key, value]) => {
    if (allowedColumns.has(key)) {
      data[key] = value;
      return;
    }

    droppedKeys.push(key);
  });

  return {
    data: data as Partial<T>,
    droppedKeys,
    tableKnown: true,
  };
};

export const sanitizeLocalTableRows = <T extends Record<string, any>>(
  tableName: string,
  rows: T[]
): { rows: Array<Partial<T>>; droppedKeys: string[]; tableKnown: boolean } => {
  const droppedKeys = new Set<string>();
  let tableKnown = true;

  const sanitizedRows = rows.map((row) => {
    const sanitized = sanitizeLocalTablePayload(tableName, row);
    tableKnown = tableKnown && sanitized.tableKnown;
    sanitized.droppedKeys.forEach((key) => droppedKeys.add(key));
    return sanitized.data;
  });

  return {
    rows: sanitizedRows,
    droppedKeys: Array.from(droppedKeys),
    tableKnown,
  };
};
